import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  Avatar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import BusinessIcon from '@material-ui/icons/Business';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';
import { exportToCsv } from '../../utils/exportCsv';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 360, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  companyAvatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 700,
  },
  companyCell: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) },
  companyName: { fontWeight: 600 },
  sub: { fontSize: '0.75rem', color: theme.palette.text.secondary },
}));

const initialsOf = (str) => {
  const s = (str || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
};

const CompaniesListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'companies'));
      setCompanies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching companies:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des entreprises', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'companies', companyToDelete.id));
      setCompanies((prev) => prev.filter((c) => c.id !== companyToDelete.id));
      setSnackbar({ open: true, message: 'Entreprise supprimée avec succès', severity: 'success' });
    } catch (err) {
      console.error('Error deleting company:', err);
      setSnackbar({ open: true, message: "Erreur lors de la suppression", severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setCompanyToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return companies.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q)
    );
  }, [companies, searchTerm]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    exportToCsv(
      filtered,
      ['name', 'email', 'phone', 'address', 'status'],
      { name: 'Nom', email: 'Email', phone: 'Téléphone', address: 'Adresse', status: 'Statut' },
      'entreprises'
    );
  };

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Entreprises' }]}
        title="Entreprises"
        subtitle={`${filtered.length} entreprise${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Exporter CSV">
              <IconButton
                onClick={handleExport}
                style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
              >
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={fetchCompanies}
                style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/companies/create')}
            >
              Nouvelle entreprise
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Rechercher par nom, email, adresse…"
            />
          </Box>
          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<BusinessIcon style={{ fontSize: 36 }} />}
            title="Aucune entreprise"
            description={
              searchTerm
                ? 'Aucune entreprise ne correspond à votre recherche.'
                : 'Commencez par créer une première entreprise.'
            }
            actionLabel={!searchTerm ? 'Créer une entreprise' : undefined}
            onAction={() => navigate('/admin/companies/create')}
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Entreprise</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Téléphone</TableCell>
                    <TableCell>Adresse</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Box className={classes.companyCell}>
                          <Avatar className={classes.companyAvatar}>
                            {initialsOf(c.name)}
                          </Avatar>
                          <Box>
                            <Box className={classes.companyName}>{c.name || '—'}</Box>
                            <Box className={classes.sub}>{c.id.slice(0, 10)}…</Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{c.email || '—'}</TableCell>
                      <TableCell>{c.phone || '—'}</TableCell>
                      <TableCell style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.address || '—'}
                      </TableCell>
                      <TableCell>
                        <StatusChip
                          status={(c.status || 'inactive').toLowerCase()}
                          label={c.status || '—'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Voir">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/companies/${c.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/companies/${c.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            style={{ color: '#ef4444' }}
                            onClick={() => { setCompanyToDelete(c); setOpenDeleteDialog(true); }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer l'entreprise <strong>{companyToDelete?.name}</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompaniesListPage;
